/**
 * Store-credit balances (issue I-19 follow-up).
 *
 * Balances are derived from the ledger entries rather than stored, so a
 * customer's balance can always be explained by the history shown beside it.
 */

import { useState } from 'react';

import * as api from '../lib/api';

export default function CreditPage() {
  const [customer, setCustomer] = useState('');
  const [record, setRecord] = useState<Awaited<ReturnType<typeof api.getCredit>> | null>(
    null,
  );
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('manual adjustment');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function lookup() {
    if (!customer.trim()) return;
    setBusy(true);
    setMessage('');
    try {
      setRecord(await api.getCredit(customer.trim()));
    } catch (err) {
      setMessage((err as api.ApiError).message);
      setRecord(null);
    } finally {
      setBusy(false);
    }
  }

  async function adjust() {
    const value = Number(amount);
    if (!customer.trim() || !value) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await api.postCredit({
        customer: customer.trim(),
        amount: value,
        reason,
      });
      setMessage(`Balance is now $${result.balance.toFixed(2)}.`);
      setAmount('');
      setRecord(await api.getCredit(customer.trim()));
    } catch (err) {
      const apiError = err as api.ApiError;
      // 409 is the "not enough credit" refusal; offer the deliberate override
      // rather than making the operator guess why it failed.
      if (apiError.status === 409 && window.confirm(`${apiError.message}\n\nAllow the balance to go negative?`)) {
        try {
          const result = await api.postCredit({
            customer: customer.trim(),
            amount: value,
            reason,
            allow_overdraw: true,
          });
          setMessage(`Balance is now $${result.balance.toFixed(2)} (overdrawn).`);
          setRecord(await api.getCredit(customer.trim()));
        } catch (retryError) {
          setMessage((retryError as api.ApiError).message);
        }
      } else {
        setMessage(apiError.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="toolbar">
        <input
          placeholder="Customer name"
          value={customer}
          onChange={(event) => setCustomer(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void lookup();
          }}
        />
        <button type="button" disabled={busy} onClick={() => void lookup()}>
          Look up
        </button>
      </div>

      {message && <p className="notice">{message}</p>}

      {record && (
        <div className="split">
          <div className="table-wrap">
            <table className="items">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Amount</th>
                  <th>Reason</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {[...record.entries].reverse().map((entry, index) => (
                  <tr key={`${entry.timestamp}-${index}`}>
                    <td className="mono small">{entry.timestamp}</td>
                    <td className={entry.amount < 0 ? 'num negative' : 'num'}>
                      {entry.amount < 0 ? '-' : '+'}${Math.abs(entry.amount).toFixed(2)}
                    </td>
                    <td>{entry.reason}</td>
                    <td className="muted small">{entry.reference}</td>
                  </tr>
                ))}
                {record.entries.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted">
                      No credit history for this customer.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <aside className="card">
            <h2>{record.customer}</h2>
            <p className={record.balance < 0 ? 'total negative' : 'total'}>
              Balance: ${record.balance.toFixed(2)}
            </p>

            <label>
              Adjustment
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="Positive adds, negative deducts"
              />
            </label>
            <label>
              Reason
              <input value={reason} onChange={(event) => setReason(event.target.value)} />
            </label>
            <button
              type="button"
              className="primary"
              disabled={busy || !Number(amount)}
              onClick={() => void adjust()}
            >
              Post adjustment
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
